import {FC, useState, useEffect, useRef} from 'react'
import {PageTitle} from '_metronic/layout/core'
import {useNavigate} from 'react-router-dom'
import Api from '../../../thesis-and-dissertations/helpers/api'
import {laporanKegiatan, panaAdmin, suratPernyataan} from '../../../services/pana'
import {toast} from 'react-toastify'
import '../../styles/pejuang.css'

const api = new Api()

interface MonevStats {
  totalWeeks: number
  completedWeeks: number
  pendingWeeks: number
  revisionWeeks: number
  lastUpdated: string | null
}

const StatusPejuangPage: FC = () => {
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [loading, setLoading] = useState(true)
  const [dataPana, setDataPana] = useState<any>(null)
  const [serverTime, setServerTime] = useState<string | null>(null)
  const [monevStats, setMonevStats] = useState<MonevStats>({
    totalWeeks: 12,
    completedWeeks: 0,
    pendingWeeks: 0,
    revisionWeeks: 0,
    lastUpdated: null
  })
  const [bukuPanduanSubstansi, setBukuPanduanSubstansi] = useState<any>(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [pdfPreviewData, setPdfPreviewData] = useState<string | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [loadingSave, setLoadingSave] = useState(false)
  const [isSuratSaved, setIsSuratSaved] = useState(false)
  const [uploadingFile, setUploadingFile] = useState(false)
  const [isSuratBermateraiUploaded, setIsSuratBermateraiUploaded] = useState(false)
  const [loadingPreviewBermaterai, setLoadingPreviewBermaterai] = useState(false)

  useEffect(() => {
    fetchData()
    fetchBukuPanduan()
  }, [])

  const fetchBukuPanduan = async () => {
    try {
      const response = await panaAdmin.getBukuPanduanActive()
      const substansiPanduan = response?.find((item: any) => item.tipe_dokumen === 'substansi')
      setBukuPanduanSubstansi(substansiPanduan)
    } catch (error) {
      console.error('Error fetching buku panduan:', error)
    }
  }

  const handleDownloadPanduan = async () => {
    try {
      const response = await panaAdmin.downloadBukuPanduan('substansi')
      const fileUrl = response.url || response.file_path
      
      if (fileUrl) {
        window.open(fileUrl, '_blank')
        toast.success('File berhasil dibuka')
      } else {
        toast.error('URL file tidak ditemukan')
      }
    } catch (error: any) {
      console.error('Error downloading file:', error)
      toast.error('Gagal mendownload file')
    }
  }

  const fetchData = async () => {
    try {
      setLoading(true)
      
      // Fetch server time
      try {
        const timeResponse: any = await api.getTimeServer()
        setServerTime(timeResponse.data?.server_time)
      } catch (error) {
        console.error('Error fetching server time:', error)
      }
      
      // Fetch data pendaftaran
      const response: any = await api.getRegisterPana()
      const panaData = response.data.data
      setDataPana(panaData)
      
      // Cek apakah surat pernyataan sudah pernah disimpan (sudah punya QR code)
      if (panaData?.is_surat_pernyataan_penerima_beasiswa == 1) {
        setIsSuratSaved(true)
      }
      
      // Cek apakah surat bermaterai sudah pernah diupload
      if (panaData?.is_surat_pernyataan_penerima_beasiswa_materai == 1) {
        setIsSuratBermateraiUploaded(true)
      }
      
      // Fetch data monev (laporan grouped by minggu)
      try {
        const monevResponse: any = await laporanKegiatan.getGroupedByMinggu()
        const apiData = monevResponse.data?.data || monevResponse.data
        
        let completed = 0
        let pending = 0
        let revision = 0
        let lastUpdate: string | null = null
        
        if (apiData && typeof apiData === 'object') {
          Object.keys(apiData).forEach((key) => {
            if (isNaN(Number(key))) return
            
            const laporanArray = apiData[key]
            if (Array.isArray(laporanArray) && laporanArray.length > 0) {
              const laporan = laporanArray[0]
              const status = Number(laporan.status)
              
              if (status == 1) {
                completed++
              } else if (status == 2) {
                revision++
              } else {
                pending++
              }
              
              // Track last update
              if (laporan.updated_at && (!lastUpdate || laporan.updated_at > lastUpdate)) {
                lastUpdate = laporan.updated_at
              }
            }
          })
        }
        
        setMonevStats({
          totalWeeks: 12,
          completedWeeks: completed,
          pendingWeeks: pending,
          revisionWeeks: revision,
          lastUpdated: lastUpdate
        })
      } catch (monevError) {
        console.error('Error fetching monev data:', monevError)
      }
      
    } catch (error: any) {
      console.error('Error fetching data:', error)
      toast.error('Gagal memuat data pendaftaran')
    } finally {
      setLoading(false)
    }
  }
  
  const formatLastUpdate = (dateString: string | null) => {
    if (!dateString) return 'Belum ada update'
    
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - date.getTime())
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays == 0) return 'Hari Ini'
    if (diffDays == 1) return 'Kemarin'
    if (diffDays < 7) return `${diffDays} hari yang lalu`
    
    return date.toLocaleDateString('id-ID', {day: 'numeric', month: 'short', year: 'numeric'})
  }
  
  const getMonevBadge = () => {
    const total = monevStats.completedWeeks + monevStats.pendingWeeks + monevStats.revisionWeeks
    
    if (total == 0) {
      return <span className='badge bg-warning'>Belum ada laporan</span>
    }
    if (monevStats.revisionWeeks > 0) {
      return <span className='badge bg-danger'>{monevStats.revisionWeeks} perlu revisi</span>
    }
    if (monevStats.pendingWeeks > 0) {
      return <span className='badge bg-primary'>{monevStats.pendingWeeks} menunggu review</span>
    }
    return <span className='badge bg-success'>{monevStats.completedWeeks} disetujui</span>
  }

  const getStatusBadge = (isSelesai: number) => {
    if (isSelesai == 1) {
      return <span className='badge bg-success px-3 py-2'>Selesai Pendaftaran</span>
    }
    return <span className='badge bg-secondary px-3 py-2'>Draft</span>
  }

  const getStatusSeleksi = (statusPenilaian: string | null, waktuPublish?: string | null) => {
    // Jika Lolos atau Tidak Lolos, cek apakah sudah waktu publish
    if ((statusPenilaian == 'Lolos' || statusPenilaian == 'Tidak Lolos') && waktuPublish && serverTime) {
      const isPublished = new Date(serverTime) >= new Date(waktuPublish)
      if (!isPublished) {
        return 'SEDANG PROSES'
      }
    }
    
    if (statusPenilaian == 'Lolos') {
      return 'Lolos'
    } else if (statusPenilaian == 'Tidak Lolos') {
      return 'Tidak Lolos'
    }
    return 'Pending'
  }

  const handleUploadSuratPenugasan = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validasi file type
      if (file.type !== 'application/pdf') {
        toast.error('File harus berformat PDF')
        return
      }
      
      // Validasi file size (max 10MB sesuai API)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Ukuran file maksimal 10MB')
        return
      }
      
      handleUploadSuratBermaterai(file)
    }
  }

  const handleUploadSuratBermaterai = async (file: File) => {
    try {
      setUploadingFile(true)
      const response: any = await suratPernyataan.uploadSuratBermaterai(file)
      
      if (response.status || response.data?.status) {
        toast.success('Surat bermaterai berhasil diupload')
        setIsSuratBermateraiUploaded(true)
        
        // Update dataPana agar is_surat_pernyataan_penerima_beasiswa_materai = 1
        setDataPana((prev: any) => ({...prev, is_surat_pernyataan_penerima_beasiswa_materai: 1}))
      } else {
        toast.error('Gagal upload surat bermaterai')
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Gagal upload surat bermaterai')
    } finally {
      setUploadingFile(false)
    }
  }

  const handlePreviewSuratBermaterai = async () => {
    try {
      setLoadingPreviewBermaterai(true)
      const response: any = await suratPernyataan.previewSuratBermaterai()
      
      // Create blob URL dari response
      let blobUrl: string
      
      if (response instanceof Blob) {
        blobUrl = URL.createObjectURL(response)
      } else if (response.data instanceof Blob) {
        blobUrl = URL.createObjectURL(response.data)
      } else if (response.data?.file || response.file) {
        // Response berupa base64
        const pdfData = response.data?.file || response.file
        window.open(pdfData, '_blank')
        toast.success('Preview surat bermaterai dibuka di tab baru')
        return
      } else {
        toast.error('Format response tidak valid')
        return
      }
      
      // Buka blob URL di tab baru
      window.open(blobUrl, '_blank')
      toast.success('Preview surat bermaterai dibuka di tab baru')
      
      // Clean up setelah beberapa detik
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000)
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Gagal membuka preview surat bermaterai')
    } finally {
      setLoadingPreviewBermaterai(false)
    }
  }

  const handleDownloadSurat = async () => {
    try {
      // Jika ada pdfPreviewData, buka di tab baru
      if (pdfPreviewData) {
        window.open(pdfPreviewData, '_blank')
        toast.success('Surat dibuka di tab baru')
      } else {
        // Call API download (response berupa file binary/blob)
        const response: any = await suratPernyataan.downloadSurat()
        
        // Create blob URL dari response
        let blobUrl: string
        
        if (response instanceof Blob) {
          // Response sudah dalam bentuk Blob
          blobUrl = URL.createObjectURL(response)
        } else if (response.data instanceof Blob) {
          // Response.data dalam bentuk Blob
          blobUrl = URL.createObjectURL(response.data)
        } else if (response.data?.file || response.file) {
          // Response berupa base64
          const pdfData = response.data?.file || response.file
          window.open(pdfData, '_blank')
          toast.success('Surat dibuka di tab baru')
          return
        } else {
          toast.error('Format response tidak valid')
          return
        }
        
        // Buka blob URL di tab baru
        window.open(blobUrl, '_blank')
        toast.success('Surat dibuka di tab baru')
        
        // Clean up setelah beberapa detik
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10000)
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Gagal membuka surat')
    }
  }

  const handleOpenEmeterai = () => {
    window.open('https://e-meterai.co.id/', '_blank')
  }

  const handleClickUpload = () => {
    fileInputRef.current?.click()
  }

  const handlePreviewSurat = async () => {
    try {
      setLoadingPreview(true)
      const response: any = await suratPernyataan.previewSurat()
      
      // Handle berbagai kemungkinan struktur response
      const pdfData = response.data?.file || response.data?.data?.file || response.file
      
      if (pdfData) {
        // Simpan data PDF dan buka modal
        setPdfPreviewData(pdfData)
        setShowPreviewModal(true)
        toast.success('Preview surat berhasil dimuat')
      } else {
        toast.error('Data PDF tidak ditemukan dalam response')
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Gagal memuat preview surat')
    } finally {
      setLoadingPreview(false)
    }
  }

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false)
    setPdfPreviewData(null)
  }

  const handleShowConfirm = () => {
    setShowConfirmModal(true)
  }

  const handleCloseConfirm = () => {
    setShowConfirmModal(false)
  }

  const handleSaveSurat = async () => {
    try {
      setLoadingSave(true)
      const response: any = await suratPernyataan.saveSurat(1)
      
      // Handle berbagai kemungkinan struktur response
      const pdfData = response.data?.file || response.data?.data?.file || response.file
      
      if (pdfData) {
        // Update PDF dengan yang ada QR code
        setPdfPreviewData(pdfData)
        setIsSuratSaved(true)
        setShowConfirmModal(false)
        
        // Update dataPana agar is_surat_pernyataan_penerima_beasiswa = 1
        setDataPana((prev: any) => ({...prev, is_surat_pernyataan_penerima_beasiswa: 1}))
        
        toast.success('Surat berhasil disimpan dengan QR code')
      } else {
        toast.error('Gagal menyimpan surat')
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Gagal menyimpan surat')
    } finally {
      setLoadingSave(false)
    }
  }

  if (loading) {
    return (
      <div className='col-md-12 text-center py-10'>
        <span className='spinner-border spinner-border-lg align-middle me-2'></span>
        <span>Memuat data...</span>
      </div>
    )
  }

  if (!dataPana) {
    return (
      <div className='col-md-12'>
        <div className='alert alert-warning'>
          <i className='bi bi-exclamation-triangle me-2'></i>
          Belum ada data pendaftaran. Silakan daftar terlebih dahulu.
        </div>
      </div>
    )
  }

  const totalSubmitted = monevStats.completedWeeks + monevStats.pendingWeeks + monevStats.revisionWeeks
  const progressPercentage = monevStats.totalWeeks > 0 
    ? Math.round((monevStats.completedWeeks / monevStats.totalWeeks) * 100) 
    : 0

  return (
    <div className='col-md-12'>
      {/* Hero Header */}
      <div className='pejuang-hero text-white mb-5'>
        <div className='row align-items-center'>
          <div className='col-lg-8'>
            <h2 className='mb-2'>Status Pendaftaran Saya</h2>
            <p className='mb-0 opacity-90'>Pantau status pendaftaran dan informasi seleksi Anda</p>
          </div>
          <div className='col-lg-4 text-lg-end mt-3 mt-lg-0'>
            <div className='d-inline-flex align-items-center bg-white bg-opacity-20 rounded-pill px-4 py-2'>
              <i className='bi bi-clock me-2'></i>
              <span>Terakhir Update: {formatLastUpdate(monevStats.lastUpdated)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Status Overview Cards */}
      <div className='row mb-5'>
        <div className='col-md-4 mb-4'>
          <div className='pejuang-stat-card'>
            <div className='stat-icon'>
              <i className='bi bi-file-earmark-text'></i>
            </div>
            <div className='stat-value' style={{fontSize: '1.5rem'}}>{dataPana.is_selesai == 1 ? 'Selesai' : 'Proses'}</div>
            <div className='stat-label'>Status Pendaftaran</div>
          </div>
        </div>
        <div className='col-md-4 mb-4'>
          <div className='pejuang-stat-card'>
            <div className='stat-icon'>
              <i className='bi bi-check-circle'></i>
            </div>
            <div className='stat-value' style={{fontSize: '1.5rem'}}>{getStatusSeleksi(dataPana.status_penilaian, dataPana.waktu_publish)}</div>
            <div className='stat-label'>Status Seleksi</div>
          </div>
        </div>
        <div className='col-md-4 mb-4'>
          <div className='pejuang-stat-card'>
            <div className='stat-icon'>
              <i className='bi bi-calendar-check'></i>
            </div>
            <div className='stat-value'>{monevStats.completedWeeks}/{monevStats.totalWeeks}</div>
            <div className='stat-label'>Minggu Penugasan</div>
          </div>
        </div>
      </div>

      {/* Profile Card */}
      <div className='pejuang-card mb-5'>
        <div className='card-header'>
          <h5 className='mb-0'>
            <i className='bi bi-person-badge'></i>
            Informasi Pendaftaran
          </h5>
        </div>
        <div className='card-body'>
          <div className='row align-items-center'>
            <div className='col-md-2 text-center mb-4 mb-md-0'>
              <div
                className='rounded-circle d-flex align-items-center justify-content-center mx-auto'
                style={{
                  width: '100px',
                  height: '100px',
                  background: 'linear-gradient(135deg, #F95E2F 0%, #FDC830 100%)',
                }}
              >
                <i className='bi bi-person fs-1 text-white'></i>
              </div>
            </div>
            <div className='col-md-6'>
              <h4 className='fw-bold mb-2'>Pendaftar PANA</h4>
              <div className='d-flex flex-wrap gap-3 mb-3'>
                <span className='text-muted'>
                  <i className='bi bi-tag me-1' style={{color: '#F95E2F'}}></i>
                  Tipe: {dataPana.tipe || '-'}
                </span>
              </div>
              <div className='d-flex flex-wrap gap-2'>
                <span className='badge bg-light text-dark'>
                  <i className='bi bi-calendar me-1' style={{color: '#F95E2F'}}></i>
                  Batch: {dataPana.id_batch || '-'}
                </span>
              </div>
            </div>
            <div className='col-md-4 mt-3 mt-md-0'>
              <div className='p-4 rounded-3' style={{background: '#FFF3EE'}}>
                <div className='text-muted small mb-1'>Judul Essay</div>
                <div className='fw-bold mb-2' style={{color: '#F95E2F'}}>{dataPana.judul_proposal || 'Belum ada judul'}</div>
                <div className='text-muted small mb-1'>No. Pendaftaran</div>
                <div className='fw-bold mb-2'>{dataPana.nomor_pendaftaran || '-'}</div>
                <div className='text-muted small mb-1'>Status</div>
                {getStatusBadge(dataPana.is_selesai)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Informasi Seleksi */}
      {dataPana.is_selesai == 1 && dataPana.status_penilaian && (
        <div className='pejuang-card mb-5'>
          <div className='card-header'>
            <h5 className='mb-0'>
              <i className='bi bi-clipboard-check'></i>
              Informasi Seleksi
            </h5>
          </div>
          <div className='card-body'>
            <div className='row'>
              <div className='col-12'>
                <div className='row g-3'>
                  <div className='col-md-12'>
                    <div className='p-3 rounded-3 border'>
                      <div className='d-flex align-items-center mb-2'>
                        <div className='rounded-circle p-2 me-2' style={{background: '#e8f5e9'}}>
                          <i className='bi bi-info-circle text-primary'></i>
                        </div>
                        <span className='text-muted small'>Status Penilaian</span>
                      </div>
                      <span className='fw-bold'>
                        {(dataPana.status_penilaian == 'Lolos' || dataPana.status_penilaian == 'Tidak Lolos') && 
                         dataPana.waktu_publish && 
                         serverTime && 
                         new Date(serverTime) < new Date(dataPana.waktu_publish)
                          ? 'SEDANG PROSES'
                          : dataPana.status_penilaian == 'Lolos Administrasi'
                          ? 'Administrasi Telah Selesai'
                          : (dataPana.status_penilaian || 'Belum ada penilaian')
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='row mt-4'>
              <div className='col-12'>
                {(dataPana.status_penilaian == 'Sudah Dinilai Administrasi' || 
                  dataPana.status_penilaian == 'Sudah Dinilai Proposal' || 
                  dataPana.status_penilaian == 'Sudah Dinilai Substansi') && 
                 dataPana.waktu_publish_wawancara && 
                 serverTime && 
                 new Date(serverTime) >= new Date(dataPana.waktu_publish_wawancara) ? (
                  <div
                    className='h-100 p-4 rounded-3 d-flex flex-column justify-content-center'
                    style={{
                      background: 'linear-gradient(135deg, #F95E2F 0%, #FDC830 100%)',
                    }}
                  >
                    <div className='text-center mb-3'>
                      <i className='bi bi-camera-video fs-1 text-white'></i>
                    </div>
                    <h6 className='text-white text-center mb-3'>Informasi Seleksi Substansi</h6>
                    
                    <div className='bg-white rounded p-3 mb-2'>
                      <div className='mb-1' style={{fontSize: '11px', color: '#666'}}>
                        <i className='bi bi-calendar-event me-2'></i>
                        Waktu Seleksi Substansi
                      </div>
                      <div className='fw-bold' style={{color: '#F95E2F', fontSize: '14px'}}>
                        {dataPana.waktu_zoom || 'Belum ditentukan'}
                      </div>
                    </div>
                    
                    <div className='bg-white rounded p-3'>
                      <div className='mb-3' style={{fontSize: '12px', color: '#333', lineHeight: '1.7'}}>
                        Gunakan kredensial berikut untuk masuk ke platform seleksi substansi sesuai dengan jadwal yang telah ditentukan:
                      </div>
                      
                      <div className='mb-3 p-3 rounded' style={{backgroundColor: '#FFF3EE', border: '1px solid #F95E2F20'}}>
                        <div className='mb-3'>
                          <div className='mb-1' style={{fontSize: '11px', color: '#666'}}>
                            <i className='bi bi-person-circle me-2'></i>
                            Username
                          </div>
                          <div className='fw-bold' style={{color: '#F95E2F', fontSize: '14px'}}>
                            {dataPana.username || 'Belum tersedia'}
                          </div>
                        </div>
                        
                        <div>
                          <div className='mb-1' style={{fontSize: '11px', color: '#666'}}>
                            <i className='bi bi-key me-2'></i>
                            Password
                          </div>
                          <div className='fw-bold' style={{color: '#F95E2F', fontSize: '14px'}}>
                            {dataPana.password || 'Belum tersedia'}
                          </div>
                        </div>
                      </div>
                      
                      <div className='alert alert-warning mb-3' style={{fontSize: '11px', padding: '10px 12px', marginBottom: '12px'}}>
                        <i className='bi bi-exclamation-triangle me-2'></i>
                        <strong>Catatan:</strong> Harap menjaga kerahasiaan data akun Anda. Jangan membagikan informasi login ini kepada pihak manapun.
                      </div>
                      
                      <div className='mb-2' style={{fontSize: '11px', color: '#666', lineHeight: '1.6'}}>
                        Seluruh panduan teknis dan informasi terkait dengan kelengkapan yang harus disiapkan dapat Anda unduh pada file berikut:
                      </div>
                      
                      {bukuPanduanSubstansi ? (
                        <button
                          onClick={handleDownloadPanduan}
                          className='btn btn-sm mt-2 w-100'
                          style={{
                            backgroundColor: '#F95E2F',
                            color: 'white',
                            border: 'none',
                            padding: '10px 16px',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          <i className='bi bi-download me-2'></i>
                          Download Panduan Teknis
                        </button>
                      ) : (
                        <button 
                          className='btn btn-sm mt-2 w-100'
                          disabled
                          style={{
                            backgroundColor: '#e0e0e0',
                            color: '#999',
                            border: 'none',
                            padding: '10px 16px',
                            fontSize: '13px'
                          }}
                        >
                          <i className='bi bi-file-earmark-text me-2'></i>
                          Panduan Belum Tersedia
                        </button>
                      )}
                    </div>
                  </div>
                ) : (dataPana.status_penilaian == 'Sudah Dinilai Administrasi' ||
                     dataPana.status_penilaian == 'Sudah Dinilai Proposal' || 
                     dataPana.status_penilaian == 'Sudah Dinilai Substansi') ? (
                  <div
                    className='h-100 p-4 text-center rounded-3 d-flex flex-column justify-content-center'
                    style={{
                      background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                    }}
                  >
                    <div className='mb-3'>
                      <i className='bi bi-clock-history fs-1 text-white'></i>
                    </div>
                    <h6 className='text-white text-opacity-75 mb-2'>Status Administrasi</h6>
                    <span className='badge bg-white text-dark fs-6 px-4 py-2 mb-3'>
                      <i className='bi bi-check-circle me-1 text-warning'></i>
                      ADMINISTRASI TELAH SELESAI
                    </span>
                    <div className='text-white small text-opacity-75'>
                      <i className='bi bi-info-circle me-1'></i>
                      Informasi seleksi substansi akan segera diumumkan
                    </div>
                  </div>
                ) : (dataPana.status_penilaian == 'Lolos' || dataPana.status_penilaian == 'Tidak Lolos') && 
                     dataPana.waktu_publish && 
                     serverTime && 
                     new Date(serverTime) >= new Date(dataPana.waktu_publish) ? (
                  <div
                    className='h-100 p-4 text-center rounded-3 d-flex flex-column justify-content-center'
                    style={{
                      background: dataPana.status_penilaian == 'Lolos'
                        ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
                        : 'linear-gradient(135deg, #dc3545 0%, #fd7e14 100%)',
                    }}
                  >
                    <div className='mb-2'>
                      <i className={`bi ${dataPana.status_penilaian == 'Lolos' ? 'bi-trophy' : 'bi-x-circle'} fs-1 text-white`}></i>
                    </div>
                    <h6 className='text-white text-opacity-75 mb-1'>Status Kelulusan</h6>
                    <span className='badge bg-white text-dark fs-6 px-4 py-2'>
                      {dataPana.status_penilaian == 'Lolos' ? (
                        <>
                          <i className='bi bi-check-circle me-1 text-success'></i>
                          LOLOS
                        </>
                      ) : (
                        <>
                          <i className='bi bi-x-circle me-1 text-danger'></i>
                          TIDAK LOLOS
                        </>
                      )}
                    </span>
                  </div>
                ) : (dataPana.status_penilaian == 'Lolos' || dataPana.status_penilaian == 'Tidak Lolos') ? (
                  <div
                    className='h-100 p-4 text-center rounded-3 d-flex flex-column justify-content-center'
                    style={{
                      background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
                    }}
                  >
                    <div className='mb-3'>
                      <i className='bi bi-hourglass-split fs-1 text-white'></i>
                    </div>
                    <h6 className='text-white text-opacity-75 mb-2'>Informasi Seleksi</h6>
                    <span className='badge bg-white text-dark fs-6 px-4 py-2 mb-3'>
                      <i className='bi bi-clock me-1 text-secondary'></i>
                      SEDANG DIPROSES
                    </span>
                    <div className='text-white small text-opacity-75'>
                      <i className='bi bi-info-circle me-1'></i>
                      Informasi seleksi akan segera diumumkan
                    </div>
                  </div>
                ) : (
                  <div
                    className='h-100 p-4 text-center rounded-3 d-flex flex-column justify-content-center'
                    style={{
                      background: 'linear-gradient(135deg, #6c757d 0%, #adb5bd 100%)',
                    }}
                  >
                    <div className='mb-3'>
                      <i className='bi bi-file-earmark-text fs-1 text-white'></i>
                    </div>
                    <h6 className='text-white text-opacity-75 mb-2'>Status Penilaian</h6>
                    <span className='badge bg-white text-dark fs-6 px-4 py-2'>
                      {(dataPana.status_penilaian == 'Lolos' || dataPana.status_penilaian == 'Tidak Lolos') && 
                       dataPana.waktu_publish && 
                       serverTime && 
                       new Date(serverTime) < new Date(dataPana.waktu_publish)
                        ? 'SEDANG PROSES'
                        : (dataPana.status_penilaian || 'Belum Ada Penilaian')
                      }
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Informasi Penempatan */}
      {dataPana.is_selesai == 1 && 
       dataPana.status_penilaian == 'Lolos' && 
       dataPana.waktu_publish && 
       serverTime && 
       new Date(serverTime) >= new Date(dataPana.waktu_publish) && (
        <div className='pejuang-card mb-5'>
          <div className='card-header'>
            <h5 className='mb-0'>
              <i className='bi bi-geo-alt'></i>
              Informasi Penempatan
            </h5>
          </div>
          <div className='card-body'>
            <div className='row'>
              <div className='col-lg-8'>
                <div className='row g-4'>
                  <div className='col-md-12'>
                    {dataPana?.penilaian_manual?.penempatan && dataPana?.penilaian_manual?.locus ? (
                      <>
                        <div className='alert alert-success border-0 shadow-sm mb-4'>
                          <div className='d-flex align-items-start'>
                            <i className='bi bi-check-circle-fill text-success fs-3 me-3'></i>
                            <div>
                              <h6 className='mb-2 fw-bold'>Selamat! Penempatan Anda Telah Ditentukan</h6>
                              <p className='mb-0 small'>Silakan cek detail lokasi dan periode penugasan Anda di bawah ini.</p>
                            </div>
                          </div>
                        </div>

                        <div className='card border-0 shadow-sm mb-4'>
                          <div className='card-body p-4'>
                            <div className='row g-3'>
                              <div className='col-md-12'>
                                <label className='text-muted small mb-1'>
                                  <i className='bi bi-building me-1'></i>
                                  Tempat Penugasan
                                </label>
                                <h5 className='fw-bold mb-0'>{dataPana.penilaian_manual.penempatan}</h5>
                              </div>
                              
                              <div className='col-md-12'>
                                <label className='text-muted small mb-1'>
                                  <i className='bi bi-geo-alt me-1'></i>
                                  Lokasi (Locus)
                                </label>
                                <h6 className='mb-0'>
                                  {dataPana.penilaian_manual.locus?.locus}
                                  {dataPana.penilaian_manual.locus?.idLocus && (
                                    <span className='badge bg-light text-dark ms-2'>{dataPana.penilaian_manual.locus.idLocus}</span>
                                  )}
                                </h6>
                              </div>

                              <div className='col-md-6'>
                                <label className='text-muted small mb-1'>
                                  <i className='bi bi-calendar-check me-1'></i>
                                  Tanggal Mulai
                                </label>
                                <p className='mb-0 fw-semibold'>
                                  {dataPana.penilaian_manual.locus?.tanggal_mulai 
                                    ? new Date(dataPana.penilaian_manual.locus.tanggal_mulai).toLocaleDateString('id-ID', {
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric'
                                      })
                                    : '-'
                                  }
                                </p>
                              </div>

                              <div className='col-md-6'>
                                <label className='text-muted small mb-1'>
                                  <i className='bi bi-calendar-x me-1'></i>
                                  Tanggal Akhir
                                </label>
                                <p className='mb-0 fw-semibold'>
                                  {dataPana.penilaian_manual.locus?.tanggal_akhir 
                                    ? new Date(dataPana.penilaian_manual.locus.tanggal_akhir).toLocaleDateString('id-ID', {
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric'
                                      })
                                    : '-'
                                  }
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className='alert alert-info'>
                        <i className='bi bi-info-circle me-2'></i>
                        Informasi penempatan akan diumumkan setelah proses seleksi selesai.
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className='mt-4 p-3 rounded-3 bg-light'>
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <span className='fw-bold'>Progress Penugasan</span>
                    <span className='badge bg-primary'>{progressPercentage}%</span>
                  </div>
                  <div className='pejuang-progress'>
                    <div className='pejuang-progress-bar' style={{width: `${progressPercentage}%`}}></div>
                  </div>
                  <div className='text-muted small mt-1'>
                    {totalSubmitted == 0 
                      ? 'Belum ada laporan yang disubmit' 
                      : `${monevStats.completedWeeks} disetujui, ${monevStats.pendingWeeks} menunggu review, ${monevStats.revisionWeeks} perlu revisi`
                    }
                  </div>
                </div>
              </div>
              <div className='col-lg-4 mt-4 mt-lg-0'>
                <div className='p-4 rounded-3 text-center h-100 d-flex flex-column justify-content-center' style={{background: '#FFF3EE'}}>
                  <div className='mb-3'>
                    <div className='rounded-circle d-inline-flex align-items-center justify-content-center p-4' style={{background: 'linear-gradient(135deg, #F95E2F 0%, #FDC830 100%)'}}>
                      <i className='bi bi-file-earmark-pdf fs-1 text-white'></i>
                    </div>
                  </div>
                  <h6 className='fw-bold mb-2'>Surat Pernyataan</h6>
                  <p className='text-dark small mb-4 fw-bold'>Surat pernyataan harus disetujui dan ditandatangani (bermaterai) selambatnya pada tanggal 13/03/2026. Jika tidak ditandatangani sampai tanggal tersebut maka dianggap telah mengundurkan diri</p>
                  <input 
                    type='file' 
                    ref={fileInputRef}
                    style={{display: 'none'}}
                    accept='application/pdf'
                    onChange={handleUploadSuratPenugasan}
                  />
                  <div className='d-flex flex-column gap-3'>
                    {!isSuratSaved ? (
                      <button 
                        className='btn btn-outline-orange btn-sm w-100'
                        onClick={handlePreviewSurat}
                        disabled={loadingPreview}
                      >
                        {loadingPreview ? (
                          <>
                            <span className='spinner-border spinner-border-sm me-2'></span>
                            Memuat...
                          </>
                        ) : (
                          <>
                            <i className='bi bi-eye me-2'></i>
                            Lihat Selengkapnya
                          </>
                        )}
                      </button>
                    ) : (
                      <>
                        <div className='alert alert-success p-2 mb-0 small'>
                          <i className='bi bi-check-circle-fill me-1'></i>
                          Surat sudah memiliki QR code
                        </div>
                        
                        <button 
                          className='btn btn-outline-orange btn-sm w-100'
                          onClick={handleDownloadSurat}
                        >
                          <i className='bi bi-download me-2'></i>
                          Download Surat
                        </button>
                        
                        <button 
                          className='btn btn-outline-orange btn-sm w-100'
                          onClick={handleOpenEmeterai}
                        >
                          <i className='bi bi-box-arrow-up-right me-2'></i>
                          Buka E-Meterai
                        </button>
                        
                        <button 
                          className='btn btn-outline-orange btn-sm w-100'
                          onClick={handleClickUpload}
                          disabled={uploadingFile}
                        >
                          {uploadingFile ? (
                            <>
                              <span className='spinner-border spinner-border-sm me-2'></span>
                              Mengupload...
                            </>
                          ) : (
                            <>
                              <i className='bi bi-upload me-2'></i>
                              Upload Surat
                            </>
                          )}
                        </button>
                        
                        {isSuratBermateraiUploaded && (
                          <button 
                            className='btn btn-outline-orange btn-sm w-100'
                            onClick={handlePreviewSuratBermaterai}
                            disabled={loadingPreviewBermaterai}
                          >
                            {loadingPreviewBermaterai ? (
                              <>
                                <span className='spinner-border spinner-border-sm me-2'></span>
                                Memuat...
                              </>
                            ) : (
                              <>
                                <i className='bi bi-eye me-2'></i>
                                Preview Surat Bermaterai
                              </>
                            )}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Menu Penugasan */}
      {dataPana.is_selesai == 1 && 
       dataPana.status_penilaian == 'Lolos' && 
       dataPana.waktu_publish && 
       serverTime && 
       new Date(serverTime) >= new Date(dataPana.waktu_publish) && (
        <div className='pejuang-card'>
          <div className='card-header'>
            <h5 className='mb-0'>
              <i className='bi bi-grid'></i>
              Menu Penugasan
            </h5>
          </div>
          <div className='card-body'>
            <div className='row g-4'>
              <div className='col-md-12'>
                <div
                  className='pejuang-wilayah-card h-100'
                  style={{cursor: 'pointer'}}
                  onClick={() => navigate('/pana-monev')}
                >
                  <div className='d-flex align-items-center'>
                    <div className='wilayah-icon' style={{background: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)'}}>
                      <i className='bi bi-bar-chart text-white'></i>
                    </div>
                    <div>
                      <div className='wilayah-name' style={{color: '#4CAF50'}}>Monev Mingguan</div>
                      <div className='wilayah-province'>Laporan kegiatan mingguan</div>
                    </div>
                  </div>
                  <div className='mt-3 pt-3 border-top'>
                    <div className='d-flex justify-content-between align-items-center'>
                      <div>
                        {getMonevBadge()}
                      </div>
                      <i className='bi bi-arrow-right fs-4' style={{color: '#4CAF50'}}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Preview PDF */}
      {showPreviewModal && (
        <div 
          className='modal fade show d-block' 
          style={{backgroundColor: 'rgba(0,0,0,0.5)'}}
          onClick={handleClosePreviewModal}
        >
          <div 
            className='modal-dialog modal-xl modal-dialog-centered'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>
                  <i className='bi bi-file-earmark-pdf me-2'></i>
                  Preview Surat Pernyataan Penerima Beasiswa
                </h5>
                <button 
                  type='button' 
                  className='btn-close' 
                  onClick={handleClosePreviewModal}
                ></button>
              </div>
              <div className='modal-body p-0' style={{height: '80vh'}}>
                {pdfPreviewData ? (
                  <iframe
                    src={pdfPreviewData}
                    width='100%'
                    height='100%'
                    style={{border: 'none'}}
                    title='Preview Surat Pernyataan'
                  />
                ) : (
                  <div className='d-flex justify-content-center align-items-center h-100'>
                    <span className='spinner-border spinner-border-lg'></span>
                  </div>
                )}
              </div>
              <div className='modal-footer'>
                {!isSuratSaved ? (
                  <>
                    <button 
                      type='button' 
                      className='btn btn-secondary'
                      onClick={handleClosePreviewModal}
                    >
                      <i className='bi bi-x-circle me-2'></i>
                      Tutup
                    </button>
                    <button 
                      type='button' 
                      className='btn btn-primary'
                      onClick={handleShowConfirm}
                      style={{background: '#F95E2F', borderColor: '#F95E2F'}}
                    >
                      <i className='bi bi-check-circle me-2'></i>
                      Saya Setuju
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      type='button' 
                      className='btn btn-secondary'
                      onClick={handleClosePreviewModal}
                    >
                      <i className='bi bi-x-circle me-2'></i>
                      Tutup
                    </button>
                    <div className='alert alert-success mb-0 py-2 px-3'>
                      <i className='bi bi-check-circle-fill me-2'></i>
                      Surat sudah disimpan dengan QR code
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Konfirmasi Persetujuan */}
      {showConfirmModal && (
        <div 
          className='modal fade show d-block' 
          style={{backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1055}}
          onClick={handleCloseConfirm}
        >
          <div 
            className='modal-dialog modal-dialog-centered'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='modal-content'>
              <div className='modal-header border-0 pb-0'>
                <button 
                  type='button' 
                  className='btn-close' 
                  onClick={handleCloseConfirm}
                ></button>
              </div>
              <div className='modal-body text-center px-5 pb-5'>
                <div className='mb-4'>
                  <i className='bi bi-question-circle text-warning' style={{fontSize: '4rem'}}></i>
                </div>
                <h5 className='mb-3'>Konfirmasi Persetujuan</h5>
                <p className='text-muted mb-4'>
                  Apakah Anda yakin ingin menyetujui surat pernyataan ini? 
                  Setelah disetujui, surat akan disimpan dengan QR code dan 
                  Anda dapat mendownload untuk dibubuhi materai.
                </p>
                <div className='d-flex gap-2'>
                  <button
                    type='button'
                    className='btn btn-light flex-fill'
                    onClick={handleCloseConfirm}
                    disabled={loadingSave}
                  >
                    Batal
                  </button>
                  <button
                    type='button'
                    className='btn btn-primary flex-fill'
                    style={{background: '#F95E2F', borderColor: '#F95E2F'}}
                    onClick={handleSaveSurat}
                    disabled={loadingSave}
                  >
                    {loadingSave ? (
                      <>
                        <span className='spinner-border spinner-border-sm me-2'></span>
                        Menyimpan...
                      </>
                    ) : (
                      <>
                        <i className='bi bi-check-circle me-2'></i>
                        Ya, Setujui
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const StatusPejuang: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'PANA', path: '/pana-beranda', isSeparator: false, isActive: false},
          {title: '/', path: '', isSeparator: false, isActive: false},
        ]}
      >
        Status Saya
      </PageTitle>
      <StatusPejuangPage />
    </>
  )
}

export default StatusPejuang
